import { flattenPageResult } from '@/utils/listPage';
import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { Modal, message, notification } from 'antd';
import { serializeStrictQueryParams } from '@/utils/strictQueryParams';
import { deepTrim } from '@/utils/trimRequest';
import { startRequestPageLoading, finishRequestPageLoading } from '@/utils/pageLoading';
import { applyReadRequestDeadline, INHERIT_READ_TIMEOUT } from '@/utils/readRequestDeadline';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  message?: string;  // ✅ 后端返回的错误消息字段
  errorCode?: number;
  errorMessage?: string;  // 兼容旧字段
  showType?: ErrorShowType;
}

const loginPath = '/user/login';

/** 与 request 拦截器一致：供原生 fetch / XHR / SSE 使用 */
export function getStoredAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  const t = localStorage.getItem('token') || sessionStorage.getItem('token');
  return t && t.trim() ? t.trim() : null;
}

// 全局标志，防止重复弹出登录过期提示
let isShowingLoginModal = false;
let lastStatusZeroToastAt = 0;

/**
 * 显示登录过期弹窗（供非 request 请求使用，如 XHR/fetch 的 401 处理）
 */
export const showLoginExpiredModal = (errorMessage?: string) => {
  // 防止重复弹窗
  if (isShowingLoginModal) {
    console.log('⚠️ [Token过期] 登录弹窗已显示，忽略重复请求');
    return;
  }
  
  isShowingLoginModal = true;
  console.warn('⚠️ [Token过期] 弹出登录提示');
  
  // 清除token和用户信息
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  // 触发自定义事件，通知应用清除用户信息
  window.dispatchEvent(new CustomEvent('logout'));
  
  // 使用Modal弹框提示用户，点击确定后跳转到登录页
  Modal.warning({
    title: '登录状态已失效',
    content: errorMessage || '您的登录状态已过期，请重新登录',
    okText: '立即登录',
    onOk: () => {
      isShowingLoginModal = false;
      const { pathname, search, hash } = window.location;
      // PDA 小应用 401 跳其专属登录页，而不是 PC 登录页
      if (pathname.startsWith('/pda')) {
        if (pathname !== '/pda/login') history.replace({ pathname: '/pda/login' });
      } else if (pathname !== loginPath) {
        history.replace({
          pathname: loginPath,
          search: new URLSearchParams({ redirect: pathname + search + hash }).toString(),
        });
      }
    },
    onCancel: () => {
      isShowingLoginModal = false;
    },
    afterClose: () => {
      isShowingLoginModal = false;
    },
    // 设置为最高优先级，确保在所有内容之上
    zIndex: 99999,
    centered: true,
    maskClosable: false,
    keyboard: false,
  });
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  timeout: INHERIT_READ_TIMEOUT,
  paramsSerializer: serializeStrictQueryParams,
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, message, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        // ✅ 优先使用 message 字段（后端返回的标准字段），如果没有则使用 errorMessage（兼容旧格式）
        const errorMsg = message || errorMessage || '操作失败';
        const error: any = new Error(errorMsg);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: errorMsg, message: errorMsg, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      finishRequestPageLoading(error?.config || error?.response?.config || opts);
      if (opts?.skipErrorHandler) throw error;
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') return;
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        message.error('数据加载超时，请重试或刷新页面');
        return;
      }
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          // ✅ 优先使用 message 字段，如果没有则使用 errorMessage（兼容旧格式）
          const errorMsg = (errorInfo as any).message || errorInfo.errorMessage || '操作失败';
          const { errorCode } = errorInfo;
          // 处理401未授权错误（token过期或未登录）
          if (errorCode === 401) {
            showLoginExpiredModal(errorMsg);
            return; // 直接返回，不显示message
          }
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMsg);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMsg);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMsg,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMsg);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const status = error.response.status;
        // status=0 通常表示浏览器层网络异常/请求被取消（非后端业务报错），避免刷屏
        if (status === 0) {
          const now = Date.now();
          if (now - lastStatusZeroToastAt > 3000) {
            message.warning('网络连接不稳定或请求被中断，请稍后重试');
            lastStatusZeroToastAt = now;
          }
          return;
        }
        // 处理HTTP 401未授权错误
        if (status === 401) {
          const result = error.response.data;
          showLoginExpiredModal(result?.message || result?.errorMessage || '您的登录状态已过期，请重新登录');
          return; // 直接返回，不显示message
        }
        message.error(`Response status:${status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      applyReadRequestDeadline(config);
      // 拦截请求配置，进行个性化处理。
      const token = getStoredAuthToken();
      // 如果有 token，添加到 Authorization header
      if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      startRequestPageLoading(config);
      return config;
    },
    // 递归去除请求参数 / 请求体的前后空格（搜索、新增、编辑统一覆盖）
    (config: RequestOptions) => {
      const opts = config as any;
      // 单请求出口：传 { skipTrim: true } 可绕过（如导入原始数据）
      if (opts.skipTrim) return config;
      if (opts.params) opts.params = deepTrim(opts.params);
      // 顶层 data 为字符串（原始 JSON 体）时不整段 trim，避免破坏结构
      if (opts.data && typeof opts.data !== 'string') {
        opts.data = deepTrim(opts.data);
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    // 必须先收尾再进入业务错误/401处理，取消和skipErrorHandler同样释放。
    [
      (response: any) => {
        finishRequestPageLoading(response.config);
        return response;
      },
      (error: any) => {
        finishRequestPageLoading(error?.config || error?.response?.config);
        return Promise.reject(error);
      },
    ],
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      // 检查业务错误码401（未授权）
      // 支持两种格式：1. data本身就是Result对象  2. data.data是Result对象
      const resultData = (data as any)?.data || data;
      
      // 调试日志：检查响应数据结构
      if (resultData?.success === false) {
        console.log('🔍 [Token检查] 请求失败，errorCode:', resultData?.errorCode, '完整响应:', resultData);
      }
      
      if (resultData?.success === false && resultData?.errorCode === 401) {
        // ✅ 优先使用 message 字段
        const errorMsg = resultData.message || resultData.errorMessage || '您的登录状态已过期，请重新登录';
        
        // 心跳先核对响应是否仍对应当前登录，避免旧请求清除重新登录后的 token。
        // 仍然抛出业务错误，由请求调用方决定是否显示提示和清理登录。
        if (!(response.config as RequestOptions)?.handleAuthFailureLocally) {
          console.warn('⚠️ [Token过期] 检测到401错误，即将弹出登录提示');
          showLoginExpiredModal(errorMsg);
        }
        
        // 抛出错误以阻止后续处理（errorHandler会捕获但不会再显示message）
        const error: any = new Error('登录已过期');
        error.name = 'BizError';
        error.info = { 
          errorCode: 401, 
          message: errorMsg,
          errorMessage: errorMsg,
          showType: ErrorShowType.SILENT // 设置为静默，不显示message
        };
        throw error;
      }

      if (data?.success === false) {
        // 不显示"请求失败"，让各个业务接口自己处理错误提示
        // message.error('请求失败！');
      }
      
      // 扁平化分页响应格式
      // 后端返回: { success: true, data: { data: [...], total: 100, current: 1, pageSize: 10 } }
      // 前端期望: { success: true, data: [...], total: 100, current: 1, pageSize: 10 }
      const flattened = flattenPageResult(data);
      if (flattened !== data) return { ...response, data: flattened } as any;
      
      return response;
    },
  ],
};
